// @flow

import * as React from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { Flex, Box, useSystem } from 'react-system';
import { IconButton } from '@material-ui/core';
import { graphql, useFragment } from 'react-relay';
import { fitBounds } from '@realadvisor/google-maps';
import { Close, Warning } from '../../icons';
import { useRouter } from '../../controls/router';
import { FilterProvider } from '../../controls/Filter/filterContext';
import { useLocale } from '../../hooks/locale';
import { useUserData } from '../../hooks/user-data';
import { useTheme } from '../../theme';
import { getPlaceTitle } from '../translate/place';
import { hasPassiveEventsSupport } from '../../utils/features-detector';
import { COUNTRY_BOUNDS } from '../controls/GeogMap';
import { toPageHref } from '../../utils/rewrites';
import { BuilderComponent } from '../builder-component';
import { Footer } from '../Layout';
import { pageDescriptor, showMapDescriptor } from './Filters/sharedDescriptors';
import { AggregatesListingsSEOLinks } from './AggregatesListingsSEOLinks';
import { AggregatesListingsBreadcrumbs } from './AggregatesListingsBreadcrumbs';
import { AggregatesListingsAnalytics } from './AggregatesListingsAnalytics';
import { AggregatesListingsFAQ } from './AggregatesListingsFAQ';
import { AggregatesListingsPageMeta } from './AggregatesListingsPageMeta';
import { useAggregatesListingsPlaceData } from './useAggregatesListingsPlaceData';
import { useSelector, useActions, useAlertDialogState } from './state';

import {
  getItemsPerPage,
  listingsDescriptor,
  listingsMobileListDescriptor,
} from './listingsDescriptor';
import { AggregatesListingsItems } from './AggregatesListingsItems';
import { AggregatesListingsSimilarItems } from './AggregatesListingsSimilarItems';
import { LISTINGS_LIST_MAX_WIDTH } from './consts';
import type { AggregatesListings_root$key } from './__generated__/AggregatesListings_root.graphql';
import type { AggregatesListings_page$key } from './__generated__/AggregatesListings_page.graphql';

const AggregatesListingsFilters = dynamic(
  () =>
    import('./AggregatesListingsFilters').then(
      module => module.AggregatesListingsFilters,
    ),
  { ssr: false, loading: () => <Box height={['133px', '75px']} /> },
);

const AggregatesListingAlertDialog = dynamic(
  () =>
    import('./aggregates-listing-alert-dialog').then(
      module => module.AggregatesListingAlertDialog,
    ),
  { ssr: false },
);

const AggregatesListingsMobileButtons = dynamic(
  () =>
    import('./AggregatesListingsMobileButtons').then(
      module => module.AggregatesListingsMobileButtons,
    ),
  { ssr: false },
);

const AggregatesListingSort = dynamic(
  () =>
    import('./AggregatesListingSort').then(
      module => module.AggregatesListingSort,
    ),
  { ssr: false, loading: () => <div css={{ height: '36px' }}>&nbsp;</div> },
);

const AggregatesListingsMapContainer = dynamic(
  () =>
    import('./AggregatesListingsMap').then(
      module => module.AggregatesListingsMapContainer,
    ),
  { ssr: false },
);

const useLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const ListingsContainer = ({ children, showMap }) => {
  const { media } = useSystem();

  // Map is displayed on desktop by default
  const mapDisplayed = showMap === true || showMap == null;

  return (
    <Box
      // onWheel={onWheel}
      width={[1, mapDisplayed ? 1 - MAP_WIDTH : 1]}
      p={3}
      css={[
        {
          maxWidth: mapDisplayed ? LISTINGS_LIST_MAX_WIDTH : 'none',
          display: 'flex',
          flexDirection: 'column',
        },
        showMap === true ? media({ visibility: ['hidden', 'visible'] }) : {},
      ]}
    >
      {children}
    </Box>
  );
};

export type MobileMapState = {| mode: boolean, modeWasSwitched: boolean |};
export type MobileMapActions = {| type: 'SWITCH_MAP_MODE' |};

const ScrollManager = () => {
  const runOnceRef = React.useRef(false);
  const hash = listingsMobileListDescriptor.useHash();
  const listingsWindowScroll = useSelector(s => s.listingsWindowScroll);

  const { onListingsWindowScroll } = useActions();

  useLayoutEffect(() => {
    if (!runOnceRef.current) {
      if (hash === listingsWindowScroll.hash) {
        // this doesn't affect native browser restore mechanism
        window.scrollTo(0, listingsWindowScroll.position);
      }
      runOnceRef.current = true;
    }
  }, [listingsWindowScroll, hash]);

  React.useEffect(() => {
    const scrollOptions = hasPassiveEventsSupport()
      ? { passive: false }
      : false;
    let prevOffset = -1;

    const handleScroll = () => {
      // Preven safari from calling on hor scroll
      const currOffset = window.pageYOffset;
      if (currOffset !== prevOffset) {
        prevOffset = currOffset;
        onListingsWindowScroll(window.pageYOffset, hash);
      }
    };

    window.addEventListener('scroll', handleScroll, scrollOptions);
    return () => {
      window.removeEventListener('scroll', handleScroll, scrollOptions);
    };
  }, [onListingsWindowScroll, hash]);

  return null;
};
type Props = {|
  root: AggregatesListings_root$key,
  page: ?AggregatesListings_page$key,
  showFooter: ?boolean,
  // viewMode: 'results' | 'detail' | 'both',
|};

const MAP_WIDTH = 0.38; // +- golden ratio

const ResultsTitle = ({ count }) => {
  const { t, numberLocale } = useLocale();
  const { text } = useTheme();

  const value = numberLocale.format(count, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

  return <Box css={text.subtitle1}>{t('nResults', { value, count })}</Box>;
};

const lineCap = (lines: number) => {
  return {
    display: '-webkit-box',
    // !important fixes emotion auto unit
    WebkitLineClamp: `${lines} !important`,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    // fallback for not supported browsers
    maxHeight: '100%',
  };
};

export const AggregatesListings = (props: Props): React.Node => {
  /* eslint-disable relay/unused-fields */
  const root = useFragment(
    graphql`
      fragment AggregatesListings_root on Query {
        ...AggregatesListingsFilters_root
        ...AggregatesListingsPageMeta_root
        aggregatesListings(
          page: $page
          sortBy: $sortBy
          sortDirection: $sortDirection
          filters: $filters
        ) {
          ...AggregatesListingsAnalytics_aggregatesListings
          ...AggregatesListingsBreadcrumbs_aggregatesListings
          ...AggregatesListingsItems_aggregatesListings
            @arguments(filters: $filters)
          ...useAggregatesListingsPlaceData_aggregatesListings
          ...AggregatesListingsSimilarItems_aggregatesListings
          ...AggregatesListingsMap_aggregatesListings
          ...AggregatesListingsPageMeta_aggregatesListings

          ...aggregatesListingAlertDialog_aggregatesListings
          totalCount
          similarItems {
            id
          }
          searchBounds {
            ne {
              lat
              lng
            }
            sw {
              lat
              lng
            }
          }
          searchGeometries {
            id
            geog {
              type
              coordinates
            }
          }
        }

        placeBySlug(slug: $slug, lang: $lngShort) @include(if: $includePlace) {
          ...AggregatesListingsSEOLinks_place
          ...AggregatesListingsFAQ_place
          canonicalPlace {
            slug
            lang
          }
          index
          indexRooms
        }

        aggregatorCustomPagesByPlace(
          placeSlug: $slug
          offerType: $offerType
          language: $lngShort
        ) {
          ...AggregatesListingsSEOLinks_popularSearches
        }

        country @include(if: $includeCountry) {
          ...AggregatesListingsSEOLinks_place
          ...AggregatesListingsFAQ_place
        }
      }
    `,
    props.root,
  );
  const page = useFragment(
    graphql`
      fragment AggregatesListings_page on CMSPage {
        ...AggregatesListingsFilters_page
      }
    `,
    props.page,
  );
  /* eslint-enable relay/unused-fields */
  const { colors } = useTheme();
  const router = useRouter();
  const { routes, redirectPathCookie } = useUserData();
  const { t, language } = useLocale();
  const { countryCode } = useUserData();
  const filtersColors = React.useMemo(
    () => ({
      bg: 'transparent',
      thumbBg: colors.white,
      ascColor: colors.red['500'],
      titleColor: colors.grey800,
      subTitleColor: colors.grey600,
      chipBg: colors.blue50,
      sliderColor: colors.primary.main,
    }),
    [colors],
  );
  const { media } = useSystem();
  const { text } = useTheme();

  const { showFooter = false } = props;
  const [pageState, setPageState, flush] = pageDescriptor.useState();
  const [{ showMap }] = showMapDescriptor.useState();
  const [params] = listingsDescriptor.useState();

  const asPathHref = React.useMemo(
    () => toPageHref(routes, router.asPath, language),
    [routes, router.asPath, language],
  );

  const listingModalOpen =
    asPathHref.pathname === '/listing' && asPathHref.query.listingSlug != null;

  const isRedirect = redirectPathCookie === router.asPath;
  // We support two ways to open save alert popup:
  //   1. click the button
  //   2. navigate to {listingsURL}?popup=save-alert
  // Second is used as the return URL after authentication
  //
  // We could use only `router.query` and no state,
  // but then popup would not close immediately,
  // and that may allow users to hit "save" button again.
  //
  // Also, when with state listings don't reload unnecessarily after popup is closed
  const alertDialogState = useAlertDialogState(
    router.query.popup === 'save-alert',
  );
  const [alertSource, setAlertSource] = React.useState('listings_manual');
  const [showOfflineListingWarning, setShowOfflineListingWarning] =
    React.useState(true);

  // postgresql has query time issues with big offsets, so we decided
  // to show max 20 pages per listings
  // See correponding API const at queries
  const MAX_PAGES_PER_LISTINGS = 20;

  // const itemsCount = root.aggregatesListings?.edges?.length ?? 0;
  const pagesCount = Math.min(
    MAX_PAGES_PER_LISTINGS,
    Math.ceil(
      (root.aggregatesListings?.totalCount ?? 0) /
        getItemsPerPage(params.offerType, params.compositePropertyType_eq),
    ),
  );
  const pageNumber = Math.min(Math.max(0, pageState.page), pagesCount - 1);

  const searchBounds =
    root.aggregatesListings?.searchBounds ?? COUNTRY_BOUNDS[countryCode];

  const searchGeometries = root.aggregatesListings?.searchGeometries ?? null;
  const mapParams = fitBounds(searchBounds, { width: 1000, height: 1000 });
  const mapUri =
    mapParams != null
      ? `https://www.google.com/maps/@${mapParams.center.lat},${mapParams.center.lng},${mapParams.zoom}z`
      : null;

  const { placeBySlug, aggregatorCustomPagesByPlace } = root;
  const placeData = useAggregatesListingsPlaceData({
    aggregatesListings: root.aggregatesListings ?? null,
  });

  const pageHeading = getPlaceTitle(t, placeData, 'plural');

  const totalCount = root.aggregatesListings?.totalCount ?? 0;

  /* SEO Settings */
  const SEO_PROPERTY_TYPE_TO_IGNORE = ['HOUSE_APPT', 'OTHER'];
  const SEO_SHOW_SEO_LINKS =
    !SEO_PROPERTY_TYPE_TO_IGNORE.includes(placeData.propertyType) &&
    placeData.rooms?.noIndex !== true;
  const SEO_IS_PAGE_NO_INDEX =
    pageNumber > 0 ||
    placeBySlug?.index === false ||
    SEO_PROPERTY_TYPE_TO_IGNORE.includes(placeData.propertyType) ||
    placeData.rooms?.noIndex === true;

  const canonicalHref = {
    pathname: '/listings',
    query: router.query,
  };
  if (placeBySlug?.canonicalPlace != null) {
    canonicalHref.query = {
      ...canonicalHref.query,
      placeSlugs: JSON.stringify([
        {
          slug: placeBySlug.canonicalPlace.slug,
          lang: placeBySlug.canonicalPlace.lang,
        },
      ]),
    };
  }

  if (placeData.rooms?.noIndex === true) {
    canonicalHref.query = {
      ...canonicalHref.query,
      numberOfRooms_gte: null,
      numberOfRooms_lte: null,
    };
  }

  return (
    <FilterProvider value={filtersColors}>
      <Head>
        {SEO_IS_PAGE_NO_INDEX && (
          <meta name="robots" content="noindex, noarchive, nofollow" />
        )}
        <link
          rel="canonical"
          href={router.getUrl(canonicalHref).absolute.split('?')[0]}
        />
      </Head>
      {root.aggregatesListings && (
        <AggregatesListingsPageMeta
          placeData={placeData}
          root={root}
          aggregatesListings={root.aggregatesListings}
        />
      )}

      <AggregatesListingsFilters
        root={root}
        page={page}
        showGeosearchOnMobile={!showMap}
        onAddAlertClick={() => {
          setAlertSource('listings_manual');
          alertDialogState.open();
        }}
      />

      <AggregatesListingsAnalytics
        aggregatesListings={root.aggregatesListings}
      />

      {root.aggregatesListings && (
        <AggregatesListingAlertDialog
          open={alertDialogState.isOpen}
          source={alertSource}
          params={params}
          aggregatesListings={root.aggregatesListings}
          onClose={() => {
            alertDialogState.close();

            const { popup, ...query } = router.query;
            if (popup === 'save-alert') {
              router.push({ pathname: router.pathname, query });
            }
          }}
        />
      )}

      <Flex
        flexGrow={1}
        flexDirection={['column', 'row']}
        css={{ position: 'relative' }}
      >
        <AggregatesListingsMapContainer
          showMap={showMap}
          // freeze the map when it's on the background
          frozenMap={listingModalOpen}
          searchGeometries={searchGeometries}
          searchBounds={searchBounds}
          aggregatesListings={root.aggregatesListings}
        />

        <ListingsContainer showMap={showMap}>
          <ScrollManager />

          <AggregatesListingsBreadcrumbs
            css={[media({ order: [1, 0] })]}
            aggregatesListings={root.aggregatesListings}
          />

          {showOfflineListingWarning && isRedirect && (
            <Box
              pt={[0, 1]}
              pb={[0, 1]}
              mb={[1, 0]}
              mt={3}
              pl={3}
              variant="contained"
              size="small"
              css={{
                backgroundColor: '#FFF9F9',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                borderRadius: '10px',
                border: '1px solid #C9454E',
              }}
            >
              <Flex flexDirection={['row']} alignItems="center">
                <Box>
                  <Warning fill="#d32f2f" size={23} />
                </Box>
                <Flex flexDirection={['column']} pl={3} my={2}>
                  <Box
                    css={[
                      {
                        color: '#C9454E',
                        left: '53px',
                        fontSize: '14x',
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        lineHeight: '16px',
                      },
                    ]}
                  >
                    {t('offlineListingWarning')}
                  </Box>
                  <Box
                    css={[
                      {
                        left: '53px',
                        fontSize: '12px',
                        fontFamily: 'Roboto',
                        lineHeight: '16px',
                      },
                    ]}
                  >
                    {t('offlineListingWarning1')}
                  </Box>
                </Flex>

                <Box ml="auto" mr={3}>
                  <IconButton
                    onClick={() => setShowOfflineListingWarning(false)}
                    css={{
                      backgroundColor: '#d32f2f',
                      '&:hover': {
                        backgroundColor: colors.red900,
                      },
                    }}
                    aria-label="close"
                    size="small"
                  >
                    <Close fill="white" size={16} />
                  </IconButton>
                </Box>
              </Flex>
            </Box>
          )}

          <Box
            as="h1"
            pt={[0, 3]}
            pb={[3, 1]}
            css={[lineCap(2), media([text.h6, text.h4])]}
          >
            {pageHeading}
          </Box>

          <Flex
            flexDirection={['column', 'row']}
            alignItems="center"
            justifyContent={totalCount === 0 ? 'flex-end' : 'space-between'}
          >
            {totalCount !== 0 && <ResultsTitle count={totalCount} />}
            <AggregatesListingSort />
          </Flex>

          <AggregatesListingsItems
            aggregatesListings={root.aggregatesListings}
            page={pageNumber}
            pagesCount={pagesCount}
            totalCount={root.aggregatesListings?.totalCount ?? 0}
            hasSimilarItems={
              (root.aggregatesListings?.similarItems?.length ?? 0) !== 0
            }
            onPageChange={pageNum => {
              setPageState({ page: pageNum });
              flush();
              // one of the conditional triggers
              // open next page as unlogged user => open popup
              if (pageNum > pageState.page) {
                // more comfort to a user
                setTimeout(() => {
                  setAlertSource('listings_page_navigation_click');
                  alertDialogState.conditionallyOpen(params);
                }, 1500);
              }
            }}
          />

          {(root.aggregatesListings?.similarItems?.length ?? 0) !== 0 && (
            <>
              <Box
                as="h2"
                pt={[0, 3]}
                pb={[3, 1]}
                css={[lineCap(2), media([text.h6, text.h6])]}
              >
                {t('countOfSimilarListingsNearby', {
                  count: root.aggregatesListings?.similarItems?.length,
                })}
              </Box>
              <AggregatesListingsSimilarItems
                aggregatesListings={root.aggregatesListings}
              />
            </>
          )}

          {!SEO_IS_PAGE_NO_INDEX && root.aggregatesListings && (
            <>
              <AggregatesListingsFAQ
                css={[media({ order: [2, 0] })]}
                place={root.country ?? root.placeBySlug}
                placeData={placeData}
                placeMapUri={mapUri}
              />
              <BuilderComponent
                model="aggregator-main-block"
                apiKey="5a1025ed9ae1451ebd7362f95b4b0486"
                pathname={router.asPath}
              />
              {SEO_SHOW_SEO_LINKS && (
                <AggregatesListingsSEOLinks
                  css={[media({ order: [3, 0] })]}
                  place={root.country ?? root.placeBySlug}
                  placeData={placeData}
                  popularSearches={aggregatorCustomPagesByPlace.filter(Boolean)}
                />
              )}
            </>
          )}

          {showFooter === true && (
            <Footer
              showPhoneNumber={false}
              footerNavigationColWidth={[1, 1 / 2, 1 / 4]}
              centerPx={0}
              styles={{
                footer: {
                  paddingLeft: 16,
                  paddingRight: 16,
                  marginLeft: -16,
                  marginRight: -16,
                  marginBottom: -16,
                },
              }}
            />
          )}
        </ListingsContainer>

        {/* mobile only button for map mode */}
        <AggregatesListingsMobileButtons />
      </Flex>
    </FilterProvider>
  );
};
